import React, { useState } from 'react'
import { submitFeedback } from '../api/feedbackAPI'
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  FormControl,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { CheckBox } from '@mui/icons-material'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  scanResultId: number
  orgUuid: string
  onFeedbackSubmitted?: () => void
  token: string
}

const FeedbackPostiveModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  scanResultId,
  orgUuid,
  onFeedbackSubmitted,
  token,
}) => {
  const [subject, setSubject] = useState('A great match for our organisation')
  const [message, setMessage] = useState('')
  const [consentToShare, setConsentToShare] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!subject.trim() || !message.trim()) {
      setError('Please fill out both subject and message')
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      await submitFeedback({
        scan_result_id: scanResultId,
        org_uuid: orgUuid,
        subject,
        message,
        hidden: false, // Don't hide the postive match
        consent_to_share: consentToShare,
        token,
      })

      // Reset form
      setSubject('')
      setMessage('')

      // Close modal and notify parent
      onClose()
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted()
      }
    } catch {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="liked-match-modal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          px: 4,
          borderRadius: 2,
          pt: 3,
          pb: 3,
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}
        >
          What made this match great?
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Sharing what worked well about this match will help us improve our
          database and AI recommendations for future matches.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Error submitting feedback
            </Typography>
            <Typography variant="body2">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              If this problem persists, please contact support.
            </Typography>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormControl sx={{ width: '100%', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What did you like about this match?
            </Typography>
            <TextField
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Offers a good fit for our organisation's mission and values"
              disabled={isSubmitting}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#757575',
                },
              }}
            />

            <Typography variant="subtitle2">
              Please provide more details
            </Typography>
            <Typography
              id="message-helper-text"
              variant="body2"
              sx={{ mb: 1, color: 'text.secondary' }}
            >
              What made this a good match for your organisation?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              aria-describedby="message-helper-text"
              disabled={isSubmitting}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#757575',
                },
              }}
            />

            <Typography
              id="consent-helper-text"
              variant="body2"
              sx={{ mb: 1, color: 'text.secondary' }}
            >
              Tick this box if you are okay with us sharing your feedback
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  aria-describedby="consent-helper-text"
                  checked={consentToShare}
                  onChange={(e) => setConsentToShare(e.target.checked)}
                  sx={{
                    '&.MuiCheckbox-colorPrimary': {
                      color: '#1C3045',
                    },
                    pt: 0,
                    mb: 4,
                  }}
                  key={'consent_to_share'}
                />
              }
              label="I'm happy for Fundsorter to anonymously share my feedback on social media"
              sx={{ alignItems: 'flex-start' }}
            />
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              disableRipple
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                borderColor: '#1C3045',
                border: 2,
                borderRadius: 2,
                color: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              disableRipple
              variant="contained"
              type="submit"
              disabled={isSubmitting || !subject.trim() || !message.trim()}
              sx={{
                bgcolor: '#1C3045',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                  <span>Submitting...</span>
                </Box>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}

export default FeedbackPostiveModal
